import type { ReviewAnnotationsT } from '../types';
import type { BatchItemPermissionsT, BatchItemStatusT, BatchItemT } from './types';
import { BATCH_ITEM_STATUS } from './types';
export const getBatchItems = (reviewAnnotations: ReviewAnnotationsT, selectedLabels: string[]) => {
  return Object.entries(reviewAnnotations).filter(([_, annotation]) => selectedLabels.includes(annotation.label)).map(([id, annotation]) => ({
    id: id,
    title: annotation.name,
    description: annotation?.instances?.map(instance => `${instance.key}\u00A0\u00A0${instance.startIndex} -> ${instance.endIndex}`).join(', '),
    status: {
      text: BATCH_ITEM_STATUS.PENDING,
      type: BATCH_ITEM_STATUS.PENDING
    },
    content: annotation?.attributes?.reduce((acc, attribute) => {
      const classificationAttributes = attribute.classificationAttributes;
      classificationAttributes?.forEach(classificationAttribute => {
        acc.push({
          label: classificationAttribute.question,
          value: classificationAttribute.answers.join(', ')
        });
      });
      return acc;
    }, ([] as {
      label: string;
      value: string;
    }[])),
    hidden: annotation.hidden
  }));
};
const createStatus = (statusType: BatchItemStatusT) => ({
  text: statusType,
  type: statusType
});
const getItemPositionStatus = (currentIndex: number, selectedId: string | undefined, previousSelectedId: string | undefined, batchItems: BatchItemT[]): BatchItemStatusT | null => {
  // Initially, the first item should be marked as "next" when no selection exists
  if (!selectedId && !previousSelectedId && currentIndex === 0) {
    return BATCH_ITEM_STATUS.NEXT;
  }
  if (!selectedId && !previousSelectedId) return null;
  const lastItemIndex = batchItems.length - 1;
  const adjacentIds = {
    previous: batchItems[currentIndex - 1]?.id,
    next: batchItems[currentIndex + 1]?.id
  };

  // Check if current item is adjacent to selected item
  if (selectedId) {
    const selectedIndex = batchItems.findIndex(item => item.id === selectedId);

    // Regular next/previous logic
    if (selectedId === adjacentIds.previous) return BATCH_ITEM_STATUS.NEXT;
    if (selectedId === adjacentIds.next) return BATCH_ITEM_STATUS.PREVIOUS;

    // Wrap-around logic: if selected is last item and current is first item
    if (selectedIndex === lastItemIndex && currentIndex === 0) {
      return BATCH_ITEM_STATUS.NEXT;
    }
    return null;
  }

  // Check if current item is next after previous selection (when no current selection)
  if (previousSelectedId) {
    const previousIndex = batchItems.findIndex(item => item.id === previousSelectedId);

    // Regular next logic
    if (previousIndex !== -1 && currentIndex === previousIndex + 1) {
      return BATCH_ITEM_STATUS.NEXT;
    }

    // Wrap-around logic: if previous was last item and current is first item
    if (previousIndex === lastItemIndex && currentIndex === 0) {
      return BATCH_ITEM_STATUS.NEXT;
    }
  }
  return null;
};
export const getUpdatedItem = ({
  item,
  index,
  selectedId,
  batchItems,
  permissions,
  previousSelectedId
}: {
  item: BatchItemT;
  index: number;
  selectedId: string | undefined;
  batchItems: BatchItemT[];
  permissions?: BatchItemPermissionsT;
  previousSelectedId?: string;
}): BatchItemT => {
  // Early return for selected item
  if (selectedId === item.id) {
    return {
      ...item,
      status: createStatus(BATCH_ITEM_STATUS.CURRENT)
    };
  }

  // Check positional status (next/previous)
  const positionStatus = getItemPositionStatus(index, selectedId, previousSelectedId, batchItems);
  if (positionStatus) {
    return {
      ...item,
      status: createStatus(positionStatus)
    };
  }

  // Default to pending if user can take actions, otherwise no status
  const status = permissions?.canTakeActions ? createStatus(BATCH_ITEM_STATUS.PENDING) : undefined;
  return {
    ...item,
    status
  };
};